import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import QRCode from "qrcode";
import {
  ShieldCheck, ArrowRight, Loader2, LogOut, Landmark, FileText, Wallet,
  CheckCircle2, XCircle, Clock, Plus, Download, ScrollText, Banknote,
  Repeat, Copy, PenLine, BadgeCheck, Send, Bell, Undo2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "th_securities_token";
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

const ACCOUNT_TYPES = [
  { k: "letter_of_credit", t: "Letter of Credit", d: "Documentary/commercial letter of credit issued to a designated beneficiary." },
  { k: "sblc", t: "Standby Letter of Credit (SBLC)", d: "Bank-standby credit enhancement guaranteeing performance or payment." },
  { k: "surety_bond", t: "Surety Bond", d: "Three-party guarantee assuring an obligation to the obligee." },
  { k: "proof_of_funds", t: "Proof of Funds (POF)", d: "Verifiable USD balance confirmation for transaction readiness." },
];
const TYPE_LABEL = Object.fromEntries(ACCOUNT_TYPES.map((a) => [a.k, a.t]));

const STATUS_STYLE = {
  submitted: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  approved: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  rejected: "bg-[#7a2222]/20 text-[#FF8585] border-[#7a2222]/50",
  active: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  suspended: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  closed: "bg-[#3a3a30]/40 text-[#9A9A90] border-[#3a3a30]",
};
const Badge = ({ s }) => (
  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-full border ${STATUS_STYLE[s] || STATUS_STYLE.closed}`}>{s}</span>
);

export default function SecuritiesPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || null);
  const [me, setMe] = useState(undefined);
  const [view, setView] = useState("home");
  const authAxios = useMemo(() => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);

  useEffect(() => {
    if (!token) { setMe(null); return; }
    authAxios.get("/securities/me").then((r) => { setMe(r.data); setView("dashboard"); })
      .catch(() => { localStorage.removeItem(TKEY); setToken(null); setMe(null); });
  }, [token]); // eslint-disable-line

  const onAuthed = (data) => {
    localStorage.setItem(TKEY, data.token);
    setToken(data.token);
    setMe({ ...data.user, profile: data.profile });
    setView("dashboard");
  };
  const logout = () => { localStorage.removeItem(TKEY); setToken(null); setMe(null); setView("home"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="securities-portal">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#1C1C16] sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-20">
        <button onClick={() => setView(me ? "dashboard" : "home")} className="text-left">
          <div className="font-serif text-[20px] tracking-tight leading-none">SEUTRADE FINANCIAL SERVICES</div>
          <div className="text-[9.5px] tracking-[0.24em] uppercase text-[#C9A227] mt-1">Securities Desk · Trade Finance Instruments</div>
        </button>
        <div className="flex items-center gap-4">
          <a href="/securities/marketplace" data-testid="securities-marketplace-link" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4]">Marketplace</a>
          <a href="/" data-testid="securities-home-link" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4]">← Home</a>
          {me ? (
            <>
              <span className="text-[12px] text-[#9A9A90]">{me.profile?.company || me.name}</span>
              <button onClick={logout} data-testid="securities-logout" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#C9A227] hover:text-[#e0b62e]"><LogOut size={13} /> Sign out</button>
            </>
          ) : (
            <button onClick={() => setView("auth")} data-testid="securities-signin-nav" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-[#C9A227] hover:text-[#e0b62e]">Sign in / Open Account <ArrowRight size={13} /></button>
          )}
        </div>
      </header>

      {view === "home" && <Marketing onStart={() => setView("auth")} onBorrow={() => setView("borrow")} />}
      {view === "auth" && <AuthPanel onAuthed={onAuthed} />}
      {view === "borrow" && <BorrowRequest onBack={() => setView("home")} />}
      {view === "dashboard" && me && <Dashboard authAxios={authAxios} me={me} token={token} />}
    </div>
  );
}

const Marketing = ({ onStart, onBorrow }) => (
  <main>
    <section className="px-6 py-20 max-w-5xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] mb-6"><Landmark size={12} className="text-[#C9A227]" /> Securities Desk · By Approval</div>
      <h1 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05]">Open a <span className="text-[#C9A227]">SEUTRADE Securities Loan Account.</span></h1>
      <p className="text-[#9A9A90] text-base mt-6 max-w-2xl mx-auto leading-relaxed">Apply to open a securities loan account backed by a running USD margin ledger. Request a <strong>Letter of Credit</strong>, <strong>SBLC</strong>, <strong>Surety Bond</strong>, or <strong>Proof of Funds</strong>. Once approved, your account is provisioned with a unique account number and a live balance you can monitor and export.</p>
      <button onClick={onStart} data-testid="securities-apply-cta" className="mt-9 inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors">Open a Securities Loan Account <ArrowRight size={15} /></button>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-5">
        <a href="/securities/marketplace" data-testid="securities-corporate-tradelines-cta" className="inline-flex items-center gap-2 border border-[#C9A227]/50 hover:border-[#C9A227] text-[#C9A227] px-6 py-2.5 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors"><Landmark size={14} /> Corporate Credit Tradelines <ArrowRight size={14} /></a>
        <button onClick={onBorrow} data-testid="securities-borrow-cta" className="text-[12px] uppercase tracking-[0.14em] text-[#9DB4E8] hover:text-[#c3d3f5] underline underline-offset-4">Are you a counterparty? Request to borrow collateral →</button>
      </div>
    </section>
    <section className="px-6 pb-24 max-w-5xl mx-auto grid sm:grid-cols-2 gap-5">
      {ACCOUNT_TYPES.map((c) => (
        <div key={c.k} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7">
          <div className="w-11 h-11 rounded-md bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center mb-5"><ScrollText size={18} className="text-[#C9A227]" /></div>
          <div className="font-serif text-xl mb-2">{c.t}</div>
          <p className="text-[#8A8A8A] text-sm leading-relaxed">{c.d}</p>
        </div>
      ))}
    </section>
  </main>
);

const AuthPanel = ({ onAuthed }) => {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", contact_name: "", company: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const path = mode === "register" ? "/securities/register" : "/securities/login";
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await axios.post(`${API}${path}`, body, { withCredentials: true });
      onAuthed(data);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <main className="px-6 py-16 max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => setMode("register")} data-testid="securities-tab-register" className={`text-[12px] uppercase tracking-[0.14em] font-bold pb-1 border-b-2 ${mode === "register" ? "text-[#C9A227] border-[#C9A227]" : "text-[#8A8A8A] border-transparent"}`}>Open Account</button>
        <span className="text-[#3a3a30]">/</span>
        <button onClick={() => setMode("login")} data-testid="securities-tab-login" className={`text-[12px] uppercase tracking-[0.14em] font-bold pb-1 border-b-2 ${mode === "login" ? "text-[#C9A227] border-[#C9A227]" : "text-[#8A8A8A] border-transparent"}`}>Sign In</button>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="securities-auth-form">
        {mode === "register" && (
          <>
            <Field label="Contact name" value={form.contact_name} onChange={(v) => up("contact_name", v)} testid="securities-contact-name" required />
            <Field label="Company / entity" value={form.company} onChange={(v) => up("company", v)} testid="securities-company" required />
            <Field label="Phone" value={form.phone} onChange={(v) => up("phone", v)} testid="securities-phone" />
            <Field label="Address" value={form.address} onChange={(v) => up("address", v)} testid="securities-address" />
          </>
        )}
        <Field label="Email" type="email" value={form.email} onChange={(v) => up("email", v)} testid="securities-email" required />
        <Field label="Password" type="password" value={form.password} onChange={(v) => up("password", v)} testid="securities-password" required />
        {error && <div className="text-[#FF8585] text-[12px]" data-testid="securities-auth-error">{error}</div>}
        <button type="submit" disabled={busy} data-testid="securities-auth-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : null}
          {mode === "register" ? "Create securities login" : "Sign in"}
        </button>
      </form>
    </main>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, required }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}{required && <span className="text-[#C9A227]"> *</span>}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
      className="mt-1 w-full bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
  </label>
);

const Dashboard = ({ authAxios, me, token }) => {
  const [apps, setApps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([authAxios.get("/securities/applications"), authAxios.get("/securities/accounts")]);
      setApps(a.data.applications || []);
      setAccounts(c.data.accounts || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <main className="px-6 sm:px-10 py-10 max-w-5xl mx-auto space-y-12" data-testid="securities-dashboard">
      <a href="/certificate?scope=member" data-testid="securities-certificate-link"
        className="flex items-center gap-4 rounded-lg border border-[#8A6D1F]/50 bg-gradient-to-r from-[#17120a] to-[#1f1708] px-5 py-4 hover:border-[#E4C97A] transition-colors no-underline">
        <span className="w-11 h-11 rounded-md bg-[#B8860B]/10 border border-[#8A6D1F]/50 flex items-center justify-center shrink-0"><ShieldCheck size={20} className="text-[#E4C97A]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#E4C97A]">Your Certificate of Authority</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Agent member certificate — membership in SEUTRADE (a § 508(c)(1)(A) faith-based organization) — printable &amp; eFaxable.</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-bold text-[#0B0B07] bg-[#E4C97A] px-3.5 py-2 rounded-full">View <ArrowRight size={13} /></span>
      </a>
      <section>
        <div className="flex items-center gap-3 mb-5">
          <Wallet size={18} className="text-[#3DBB8E]" />
          <h2 className="text-2xl font-serif">My Securities Accounts</h2>
        </div>
        {loading ? <Loader2 className="animate-spin text-[#8A8A8A]" /> : accounts.length === 0 ? (
          <p className="text-[#8A8A8A] text-sm" data-testid="securities-no-accounts">No provisioned accounts yet. Submit an application below — once approved, your account and USD balance appear here.</p>
        ) : (
          <div className="space-y-4">
            {accounts.map((acc) => <AccountCard key={acc.id} acc={acc} authAxios={authAxios} token={token} />)}
          </div>
        )}
      </section>

      <ApplyPanel authAxios={authAxios} onSubmitted={load} />

      <PayoutMethodForm authAxios={authAxios} me={me} />

      <StripeConnectCard authAxios={authAxios} />


      <SecuritiesLoans authAxios={authAxios} accounts={accounts} onChange={load} />

      <section>
        <div className="flex items-center gap-3 mb-5">
          <FileText size={18} className="text-[#B8860B]" />
          <h2 className="text-2xl font-serif">My Applications</h2>
        </div>
        {apps.length === 0 ? <p className="text-[#8A8A8A] text-sm">No applications submitted yet.</p> : (
          <div className="overflow-x-auto rounded-lg border border-[#2A2A22]">
            <table className="w-full text-[13px]" data-testid="securities-applications-table">
              <thead className="bg-[#13130E] text-[#8A8A8A] text-[10px] uppercase tracking-[0.12em]">
                <tr><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Requested</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Account No.</th><th className="text-left px-4 py-3">Note</th></tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} className="border-t border-[#1C1C16]" data-testid={`securities-app-row-${a.id}`}>
                    <td className="px-4 py-3">{a.type_label || TYPE_LABEL[a.account_type]}</td>
                    <td className="px-4 py-3">{fmt(a.requested_amount)}</td>
                    <td className="px-4 py-3"><Badge s={a.status} /></td>
                    <td className="px-4 py-3 font-mono text-[12px] text-[#E4C97A]">{a.account_no || "—"}</td>
                    <td className="px-4 py-3 text-[#8A8A8A]">{a.review_note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

const DunsRow = ({ acc, authAxios }) => {
  const [duns, setDuns] = useState(acc.duns_number || "");
  const [saved, setSaved] = useState(!!acc.duns_number);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await authAxios.post(`/securities/accounts/${acc.id}/duns`, { duns_number: duns });
      setSaved(true);
    } catch (e) { alert(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-[#1C1C16] px-5 py-3 bg-[#0E0E09]" data-testid={`securities-duns-${acc.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[11px] uppercase tracking-[0.12em] text-[#8A8A8A]">DUNS Onboarding</span>
        {saved ? (
          <span className="text-[12px] text-[#3DBB8E]" data-testid={`securities-duns-value-${acc.id}`}>✓ DUNS {duns} attached — prints on your Trade Reference & UCC-1</span>
        ) : (
          <>
            <input value={duns} onChange={(e) => { setDuns(e.target.value); setSaved(false); }} placeholder="15-048-3782" data-testid={`securities-duns-input-${acc.id}`}
              className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-1.5 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none w-[150px]" />
            <button onClick={save} disabled={busy || !duns} data-testid={`securities-duns-save-${acc.id}`} className="inline-flex items-center gap-1.5 bg-[#9DB4E8] hover:bg-[#c3d3f5] text-[#0A0A0A] px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold disabled:opacity-50">Attach DUNS</button>
            <a href="https://www.dnb.com/duns-number/get-a-duns.html" target="_blank" rel="noreferrer" className="text-[11px] text-[#9DB4E8] hover:text-[#c3d3f5] underline underline-offset-4">Don't have one? Request a free DUNS →</a>
          </>
        )}
      </div>
    </div>
  );
};

const AccountCard = ({ acc, authAxios, token }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const toggle = async () => {
    if (!open && !detail) {
      const r = await authAxios.get(`/securities/accounts/${acc.id}`);
      setDetail(r.data.account);
    }
    setOpen((o) => !o);
  };
  const statementUrl = `${API}/securities/accounts/${acc.id}/statement.pdf`;
  const downloadStatement = async () => {
    const r = await authAxios.get(`/securities/accounts/${acc.id}/statement.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
  };
  const payFee = async () => {
    try {
      const { data } = await authAxios.post(`/securities/accounts/${acc.id}/pay-fee`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { alert(errMsg(e)); }
  };

  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] overflow-hidden" data-testid={`securities-account-${acc.id}`}>
      <div className="p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="font-mono text-[15px] text-[#E4C97A]" data-testid={`securities-account-no-${acc.id}`}>{acc.account_no}</div>
          <div className="text-[12px] text-[#9A9A90] mt-1 flex items-center gap-2"><Banknote size={13} className="text-[#3DBB8E]" /> {acc.type_label}</div>
          <div className="text-[11px] text-[#8A8A8A] mt-1">On loan: <span className="text-[#E4C97A]">{fmt(acc.on_loan)}</span> · Available: <span className="text-[#3DBB8E]" data-testid={`securities-available-${acc.id}`}>{fmt(acc.available)}</span></div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Collateral Balance (USD)</div>
          <div className="text-2xl font-serif text-[#3DBB8E]" data-testid={`securities-balance-${acc.id}`}>{fmt(acc.balance)}</div>
        </div>
        <Badge s={acc.status} />
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <button onClick={toggle} data-testid={`securities-toggle-ledger-${acc.id}`} className="text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]">{open ? "Hide ledger" : "View ledger"}</button>
          <button onClick={downloadStatement} data-testid={`securities-statement-${acc.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><Download size={13} /> Statement PDF</button>
          <button onClick={async () => { const r = await authAxios.get(`/securities/accounts/${acc.id}/trade-reference.pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }} data-testid={`securities-trade-ref-${acc.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><FileText size={13} /> Trade Reference</button>
          <button onClick={async () => { const d = prompt("List on marketplace — discount % to face:", "6"); if (d == null) return; try { await authAxios.post("/securities/listings", { source_type: "account", source_id: acc.id, discount_pct: parseFloat(d) }); alert("Listed on the Securities Marketplace."); } catch (e) { alert(errMsg(e)); } }} data-testid={`securities-list-account-${acc.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]"><ArrowRight size={13} /> List on marketplace</button>
        </div>
      </div>
      {Number(acc.facility_fee) > 0 && !acc.facility_fee_paid && (
        <div className="border-t border-[#1C1C16] px-5 py-3 flex flex-wrap items-center gap-3 bg-[#1A1606]" data-testid={`securities-facility-fee-${acc.id}`}>
          <span className="text-[12px] text-[#E4C97A]">Facility fee due: <strong>{fmt(acc.facility_fee)}</strong> — payable to SEUTRADE to activate securities-lending.</span>
          <button onClick={payFee} data-testid={`securities-pay-fee-${acc.id}`} className="inline-flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-4 py-1.5 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">Pay facility fee</button>
        </div>
      )}
      {acc.facility_fee_paid && (
        <div className="border-t border-[#1C1C16] px-5 py-2 text-[11px] text-[#3DBB8E]" data-testid={`securities-fee-paid-${acc.id}`}>✓ Facility fee paid</div>
      )}
      <DunsRow acc={acc} authAxios={authAxios} />
      {open && detail && (
        <div className="border-t border-[#1C1C16] px-5 py-4 overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-[#8A8A8A] text-[10px] uppercase tracking-[0.1em]"><tr><th className="text-left py-2">Date</th><th className="text-left py-2">Memo</th><th className="text-right py-2">Credit</th><th className="text-right py-2">Debit</th><th className="text-right py-2">Balance</th></tr></thead>
            <tbody>
              {(detail.ledger || []).map((e) => (
                <tr key={e.id} className="border-t border-[#1C1C16]">
                  <td className="py-2 text-[#9A9A90]">{(e.created_at || "").slice(0, 10)}</td>
                  <td className="py-2">{e.memo || "—"}</td>
                  <td className="py-2 text-right text-[#3DBB8E]">{e.entry_type === "credit" ? fmt(e.amount) : ""}</td>
                  <td className="py-2 text-right text-[#FF8585]">{e.entry_type === "debit" ? fmt(e.amount) : ""}</td>
                  <td className="py-2 text-right font-mono">{fmt(e.balance_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ApplyPanel = ({ authAxios, onSubmitted }) => {
  const [form, setForm] = useState({ account_type: "letter_of_credit", requested_amount: "", purpose: "", beneficiary_name: "", beneficiary_bank: "", term_days: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setMsg(null);
    try {
      const body = {
        account_type: form.account_type,
        requested_amount: parseFloat(form.requested_amount),
        purpose: form.purpose || null,
        beneficiary_name: form.beneficiary_name || null,
        beneficiary_bank: form.beneficiary_bank || null,
        term_days: form.term_days ? parseInt(form.term_days, 10) : null,
        notes: form.notes || null,
      };
      await authAxios.post("/securities/applications", body);
      setMsg("Application submitted. It will appear below and awaits admin approval.");
      setForm({ account_type: "letter_of_credit", requested_amount: "", purpose: "", beneficiary_name: "", beneficiary_bank: "", term_days: "", notes: "" });
      onSubmitted();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <section>
      <div className="flex items-center gap-3 mb-5">
        <Plus size={18} className="text-[#C9A227]" />
        <h2 className="text-2xl font-serif">Open a New Account</h2>
      </div>
      <form onSubmit={submit} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6 grid sm:grid-cols-2 gap-4" data-testid="securities-apply-form">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Account type <span className="text-[#C9A227]">*</span></span>
          <select value={form.account_type} onChange={(e) => up("account_type", e.target.value)} data-testid="securities-account-type"
            className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
            {ACCOUNT_TYPES.map((t) => <option key={t.k} value={t.k}>{t.t}</option>)}
          </select>
        </label>
        <Field label="Requested amount (USD)" type="number" value={form.requested_amount} onChange={(v) => up("requested_amount", v)} testid="securities-requested-amount" required />
        <Field label="Beneficiary name" value={form.beneficiary_name} onChange={(v) => up("beneficiary_name", v)} testid="securities-beneficiary-name" />
        <Field label="Beneficiary bank" value={form.beneficiary_bank} onChange={(v) => up("beneficiary_bank", v)} testid="securities-beneficiary-bank" />
        <Field label="Term (days)" type="number" value={form.term_days} onChange={(v) => up("term_days", v)} testid="securities-term-days" />
        <Field label="Purpose" value={form.purpose} onChange={(v) => up("purpose", v)} testid="securities-purpose" />
        <label className="block sm:col-span-2">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Notes</span>
          <textarea value={form.notes} onChange={(e) => up("notes", e.target.value)} rows={2} data-testid="securities-notes"
            className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        </label>
        {error && <div className="sm:col-span-2 text-[#FF8585] text-[12px]" data-testid="securities-apply-error">{error}</div>}
        {msg && <div className="sm:col-span-2 text-[#3DBB8E] text-[12px]" data-testid="securities-apply-success">{msg}</div>}
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} data-testid="securities-apply-submit" className="inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-7 py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold transition-colors">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} Submit application
          </button>
        </div>
      </form>
    </section>
  );
};

const LOAN_STATUS = {
  pending_signature: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  executed: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  active: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  recall_requested: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  returned: "bg-[#3a3a30]/40 text-[#9A9A90] border-[#3a3a30]",
  terminated: "bg-[#7a2222]/20 text-[#FF8585] border-[#7a2222]/50",
};
const LoanBadge = ({ s }) => (
  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-full border ${LOAN_STATUS[s] || LOAN_STATUS.returned}`}>{(s || "").replace("_", " ")}</span>
);

const SecuritiesLoans = ({ authAxios, accounts, onChange }) => {
  const [loans, setLoans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const activeAccounts = accounts.filter((a) => a.status === "active");

  const load = async () => {
    const r = await authAxios.get("/securities/loans");
    setLoans(r.data.loans || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const refresh = () => { load(); onChange(); };

  return (
    <section data-testid="securities-loans-section">
      <div className="flex items-center gap-3 mb-5">
        <Repeat size={18} className="text-[#C9A227]" />
        <h2 className="text-2xl font-serif">Securities Loans</h2>
        {activeAccounts.length > 0 && (
          <button onClick={() => setShowForm((v) => !v)} data-testid="securities-new-loan-toggle" className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]"><Plus size={14} /> New loan</button>
        )}
      </div>
      <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Temporarily assign part or all of an account's available collateral to a counterparty under a standard, e-signed Securities Loan Agreement. The counterparty pays a lending fee; a per-loan origination cost is payable by you to SEUTRADE.</p>

      {activeAccounts.length === 0 ? (
        <p className="text-[#8A8A8A] text-sm" data-testid="securities-loans-no-account">You need an active, funded account before you can lend collateral.</p>
      ) : showForm && (
        <NewLoanForm authAxios={authAxios} accounts={activeAccounts} onCreated={() => { setShowForm(false); refresh(); }} />
      )}

      {loans.length > 0 && (
        <div className="space-y-3 mt-5">
          {loans.map((l) => <LoanCard key={l.id} loan={l} authAxios={authAxios} onChange={refresh} />)}
        </div>
      )}
    </section>
  );
};

const NewLoanForm = ({ authAxios, accounts, onCreated }) => {
  const [form, setForm] = useState({ account_id: accounts[0]?.id || "", counterparty_name: "", counterparty_entity: "", counterparty_email: "", counterparty_address: "", amount: "", term_days: 90, counterparty_fee_rate: 0.5, purpose: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const acc = accounts.find((a) => a.id === form.account_id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await authAxios.post(`/securities/accounts/${form.account_id}/loans`, {
        counterparty_name: form.counterparty_name,
        counterparty_entity: form.counterparty_entity || null,
        counterparty_email: form.counterparty_email || null,
        counterparty_address: form.counterparty_address || null,
        amount: parseFloat(form.amount),
        term_days: parseInt(form.term_days, 10),
        counterparty_fee_rate: parseFloat(form.counterparty_fee_rate),
        purpose: form.purpose || null,
      });
      onCreated();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6 grid sm:grid-cols-2 gap-4" data-testid="securities-loan-form">
      <label className="block sm:col-span-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Lend from account <span className="text-[#C9A227]">*</span></span>
        <select value={form.account_id} onChange={(e) => up("account_id", e.target.value)} data-testid="securities-loan-account"
          className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
          {accounts.map((a) => <option key={a.id} value={a.id}>{`${a.account_no} · ${a.type_label} · avail ${fmt(a.available)}`}</option>)}
        </select>
      </label>
      <Field label="Counterparty name" value={form.counterparty_name} onChange={(v) => up("counterparty_name", v)} testid="securities-loan-cp-name" required />
      <Field label="Counterparty entity" value={form.counterparty_entity} onChange={(v) => up("counterparty_entity", v)} testid="securities-loan-cp-entity" />
      <Field label="Counterparty email" type="email" value={form.counterparty_email} onChange={(v) => up("counterparty_email", v)} testid="securities-loan-cp-email" />
      <Field label="Counterparty address" value={form.counterparty_address} onChange={(v) => up("counterparty_address", v)} testid="securities-loan-cp-address" />
      <Field label={`Loan amount (USD)${acc ? ` — max ${fmt(acc.available)}` : ""}`} type="number" value={form.amount} onChange={(v) => up("amount", v)} testid="securities-loan-amount" required />
      <Field label="Term (days)" type="number" value={form.term_days} onChange={(v) => up("term_days", v)} testid="securities-loan-term" />
      <Field label="Counterparty fee rate (% annual)" type="number" value={form.counterparty_fee_rate} onChange={(v) => up("counterparty_fee_rate", v)} testid="securities-loan-fee-rate" />
      <Field label="Purpose" value={form.purpose} onChange={(v) => up("purpose", v)} testid="securities-loan-purpose" />
      {error && <div className="sm:col-span-2 text-[#FF8585] text-[12px]" data-testid="securities-loan-error">{error}</div>}
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy} data-testid="securities-loan-submit" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-7 py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : null} Create & prepare agreement
        </button>
      </div>
    </form>
  );
};

const LoanCard = ({ loan, authAxios, onChange }) => {
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState("");
  const [transmit, setTransmit] = useState({ open: false, channel: "email", to: "", busy: false });
  const lenderSigned = (loan.signatures || []).some((s) => (s.signer_role || "").toLowerCase() === "lender");
  const borrowerSigned = (loan.signatures || []).some((s) => (s.signer_role || "").toLowerCase() === "borrower");
  const signTok = loan.counterparty_sign_url ? loan.counterparty_sign_url.split("t=")[1] : "";
  const canRecall = ["executed", "active"].includes(loan.status);

  useEffect(() => {
    if (loan.verify_url) QRCode.toDataURL(loan.verify_url, { margin: 1, width: 200, errorCorrectionLevel: "H" }).then(setQr).catch(() => {});
  }, [loan.verify_url]);

  const sign = async () => {
    setSigning(true);
    try {
      await authAxios.post(`/securities/loans/${loan.id}/sign`, { signer_name: signerName, signer_title: "Account Holder" });
      onChange();
    } catch (e) { alert(errMsg(e)); } finally { setSigning(false); }
  };
  const copyLink = () => { navigator.clipboard.writeText(loan.counterparty_sign_url); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const payOrigination = async () => {
    try {
      const { data } = await authAxios.post(`/securities/loans/${loan.id}/pay-origination`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { alert(errMsg(e)); }
  };
  const agreementPdf = () => window.open(`${API}/securities/loans/${loan.id}/agreement.pdf?t=${signTok}`, "_blank");
  const recall = async () => {
    if (!window.confirm("Request recall of this securities loan?")) return;
    try { await authAxios.post(`/securities/loans/${loan.id}/recall`); onChange(); } catch (e) { alert(errMsg(e)); }
  };
  const reminder = async () => {
    try { await authAxios.post(`/securities/loans/${loan.id}/send-reminder`); alert("Maturity reminder sent to the counterparty."); onChange(); } catch (e) { alert(errMsg(e)); }
  };
  const doTransmit = async () => {
    setTransmit((t) => ({ ...t, busy: true }));
    try {
      await authAxios.post(`/securities/loans/${loan.id}/transmit`, { channel: transmit.channel, to: transmit.to });
      alert(`Agreement sent via ${transmit.channel}.`);
      setTransmit({ open: false, channel: "email", to: "", busy: false });
    } catch (e) { alert(errMsg(e)); setTransmit((t) => ({ ...t, busy: false })); }
  };

  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-5" data-testid={`securities-loan-${loan.id}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex-1 min-w-[200px]">
          <div className="font-mono text-[14px] text-[#E4C97A]">{loan.loan_no}</div>
          <div className="text-[12px] text-[#9A9A90]">To {loan.counterparty_name} · from {loan.account_no}</div>
        </div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Loaned</div><div className="text-lg font-serif text-[#C9A227]">{fmt(loan.amount)}</div></div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">CP Fee ({loan.counterparty_fee_rate}%)</div><div className="text-[13px] text-[#3DBB8E]">{fmt(loan.counterparty_fee)}</div></div>
        <LoanBadge s={loan.status} />
      </div>

      {/* Instrument being lent */}
      <div className="mt-3 rounded-md bg-[#0F0F0A] border border-[#2A2A22] px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`securities-loan-instrument-${loan.id}`}>
        <ScrollText size={16} className="text-[#C9A227]" />
        <div className="text-[12px]">
          <span className="text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]">Instrument on loan</span>
          <div className="text-[#ECECE4]"><strong className="text-[#E4C97A]">{loan.type_label}</strong> · {fmt(loan.amount)} USD{loan.beneficiary_name ? ` · Beneficiary ${loan.beneficiary_name}` : ""}</div>
        </div>
        <div className="ml-auto text-right text-[11px] text-[#9A9A90]">
          Matures {loan.maturity_date}{loan.days_to_maturity != null ? ` · ${loan.days_to_maturity}d` : ""}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className={`inline-flex items-center gap-1 ${lenderSigned ? "text-[#3DBB8E]" : "text-[#8A8A8A]"}`}><PenLine size={12} /> Lender {lenderSigned ? "signed" : "pending"}</span>
          <span className="text-[#3a3a30]">·</span>
          <span className={`inline-flex items-center gap-1 ${borrowerSigned ? "text-[#3DBB8E]" : "text-[#8A8A8A]"}`}><PenLine size={12} /> Counterparty {borrowerSigned ? "signed" : "pending"}</span>
        </div>
        {loan.notary && <span className="text-[11px] text-[#E4C97A] inline-flex items-center gap-1" data-testid={`securities-loan-notary-${loan.id}`}><BadgeCheck size={12} /> Notarized · {loan.notary.name}</span>}
        {qr && <img src={qr} alt="verify qr" className="w-14 h-14 rounded bg-white p-1" data-testid={`securities-loan-qr-${loan.id}`} title="Scan to verify" />}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {!lenderSigned && !signing && (
          <button onClick={() => setSigning(true)} data-testid={`securities-loan-sign-open-${loan.id}`} className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] text-white px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><PenLine size={13} /> E-sign as lender</button>
        )}
        {!lenderSigned && signing && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Type your full name" data-testid={`securities-loan-signer-${loan.id}`}
              className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <button onClick={sign} disabled={!signerName} data-testid={`securities-loan-sign-confirm-${loan.id}`} className="bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">Confirm</button>
          </div>
        )}
        <button onClick={copyLink} data-testid={`securities-loan-copy-link-${loan.id}`} className="inline-flex items-center gap-1.5 border border-[#2A2A22] hover:border-[#C9A227] text-[#C9A227] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em]">{copied ? <BadgeCheck size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Counterparty sign link"}</button>
        <button onClick={agreementPdf} data-testid={`securities-loan-agreement-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><FileText size={13} /> Agreement PDF</button>
        <a href={loan.verify_url} target="_blank" rel="noreferrer" data-testid={`securities-loan-verify-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#E4C97A] hover:text-[#f0d98f]"><ShieldCheck size={13} /> Verify</a>
        <button onClick={() => window.open(`${API}/securities/loans/${loan.id}/ucc1.pdf?t=${signTok}`, "_blank")} data-testid={`securities-loan-ucc1-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><FileText size={13} /> UCC-1</button>
        <button onClick={async () => { const d = prompt("List this loan on marketplace — discount % to face:", "6"); if (d == null) return; try { await authAxios.post("/securities/listings", { source_type: "loan", source_id: loan.id, discount_pct: parseFloat(d) }); alert("Listed on the Securities Marketplace."); } catch (e) { alert(errMsg(e)); } }} data-testid={`securities-list-loan-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]"><ArrowRight size={13} /> List</button>
        <button onClick={() => setTransmit((t) => ({ ...t, open: !t.open }))} data-testid={`securities-loan-transmit-toggle-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><Send size={13} /> Transmit</button>
        <button onClick={reminder} data-testid={`securities-loan-reminder-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#8A8A8A] hover:text-[#ECECE4]"><Bell size={13} /> Reminder</button>
        {canRecall && <button onClick={recall} data-testid={`securities-loan-recall-${loan.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#FF8585] hover:text-[#ffa5a5]"><Undo2 size={13} /> Recall</button>}
        {Number(loan.origination_fee) > 0 && !loan.origination_fee_paid && (
          <button onClick={payOrigination} data-testid={`securities-loan-pay-orig-${loan.id}`} className="inline-flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">Pay origination {fmt(loan.origination_fee)}</button>
        )}
        {loan.origination_fee_paid && <span className="text-[11px] text-[#3DBB8E]" data-testid={`securities-loan-orig-paid-${loan.id}`}>✓ Origination paid</span>}
      </div>

      {transmit.open && (
        <div className="mt-3 flex flex-wrap items-end gap-3 bg-[#0F0F0A] border border-[#2A2A22] rounded-md p-3" data-testid={`securities-loan-transmit-panel-${loan.id}`}>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Channel</span>
            <select value={transmit.channel} onChange={(e) => setTransmit((t) => ({ ...t, channel: e.target.value }))} data-testid={`securities-loan-transmit-channel-${loan.id}`}
              className="mt-1 block bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
              <option value="email">Email (PDF attached)</option>
              <option value="fax">eFax</option>
            </select>
          </label>
          <label className="block flex-1 min-w-[180px]">
            <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">{transmit.channel === "fax" ? "Recipient fax (E.164)" : "Recipient email"}</span>
            <input value={transmit.to} onChange={(e) => setTransmit((t) => ({ ...t, to: e.target.value }))} data-testid={`securities-loan-transmit-to-${loan.id}`}
              placeholder={transmit.channel === "fax" ? "+13125551234" : "counterparty@bank.com"}
              className="mt-1 w-full bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          </label>
          <button onClick={doTransmit} disabled={transmit.busy || !transmit.to} data-testid={`securities-loan-transmit-send-${loan.id}`} className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
            {transmit.busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
          </button>
        </div>
      )}
    </div>
  );
};


const StripeConnectCard = ({ authAxios }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const refresh = async () => {
    try { const { data } = await authAxios.get("/securities/connect/status"); setStatus(data); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line
  const onboard = async () => {
    setBusy(true); setNote("");
    try {
      const { data } = await authAxios.post("/securities/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setNote(typeof detail === "string" ? detail : "Automatic payouts aren't available right now. Please try again later.");
      setBusy(false);
    }
  };
  const ready = status?.payouts_enabled;
  return (
    <section data-testid="securities-connect-section" className="mt-8">
      <div className="flex items-center gap-3 mb-4">
        <Landmark size={18} className="text-[#635BFF]" />
        <h2 className="text-2xl font-serif">Automatic Payouts</h2>
        {ready && <span className="text-[10px] uppercase tracking-[0.12em] text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full px-2.5 py-1">Connected</span>}
      </div>
      <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6" data-testid="securities-connect-card">
        {ready ? (
          <p className="text-[13px] text-[#3DBB8E]" data-testid="securities-connect-ready">✓ Your Stripe payout account is connected. Collected counterparty lending fees are transferred to you automatically.</p>
        ) : (
          <>
            <p className="text-[13px] text-[#9A9A90] mb-4">Connect a Stripe payout account to receive your lending fees automatically. You'll securely enter your identity and bank details on Stripe — we never see them.{status?.details_submitted && !ready ? " Your details are under review by Stripe." : ""}</p>
            <button onClick={onboard} disabled={busy} data-testid="securities-connect-btn" className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5249e0] text-white px-6 py-3 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Landmark size={15} />} {status?.connected ? "Continue Stripe onboarding" : "Connect payouts with Stripe"}
            </button>
            {note && <p className="mt-3 text-[12px] text-[#E4C97A]" data-testid="securities-connect-note">{note}</p>}
          </>
        )}
      </div>
    </section>
  );
};

const PayoutMethodForm = ({ authAxios, me }) => {
  const existing = me?.profile?.payout_method;
  const [form, setForm] = useState({ method_type: "bank", account_name: "", bank_name: "", routing_number: "", account_number: "", card_number: "", card_exp: "" });
  const [saved, setSaved] = useState(existing || null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await authAxios.post("/securities/payout-method", form);
      setSaved(data.payout_method); setOpen(false);
    } catch (err) { alert(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <section data-testid="securities-payout-section">
      <div className="flex items-center gap-3 mb-3">
        <Banknote size={18} className="text-[#3DBB8E]" />
        <h2 className="text-2xl font-serif">Payout Method</h2>
        <button onClick={() => setOpen((v) => !v)} data-testid="securities-payout-toggle" className="ml-auto text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]">{saved ? "Update" : "Add payout method"}</button>
      </div>
      <p className="text-[#8A8A8A] text-sm mb-4 max-w-2xl">Add your bank or debit-card details so collected counterparty lending fees are automatically remitted to you when a deal is paid. Only the last 4 digits are stored.</p>
      {saved && !open && (
        <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] px-5 py-3 text-[13px]" data-testid="securities-payout-saved">
          <span className="text-[#3DBB8E]">✓ On file:</span> {saved.method_type === "card" ? `Debit card ····${saved.card_last4}` : `${saved.bank_name || "Bank"} ····${saved.account_last4}`} · {saved.account_name}
        </div>
      )}
      {open && (
        <form onSubmit={submit} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6 grid sm:grid-cols-2 gap-4" data-testid="securities-payout-form">
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Method</span>
            <select value={form.method_type} onChange={(e) => up("method_type", e.target.value)} data-testid="securities-payout-type"
              className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
              <option value="bank">Bank account (ACH)</option>
              <option value="card">Debit card</option>
            </select>
          </label>
          <Field label="Account holder name" value={form.account_name} onChange={(v) => up("account_name", v)} testid="securities-payout-name" required />
          {form.method_type === "bank" ? (
            <>
              <Field label="Bank name" value={form.bank_name} onChange={(v) => up("bank_name", v)} testid="securities-payout-bank" />
              <Field label="Routing number" value={form.routing_number} onChange={(v) => up("routing_number", v)} testid="securities-payout-routing" />
              <Field label="Account number" value={form.account_number} onChange={(v) => up("account_number", v)} testid="securities-payout-account" />
            </>
          ) : (
            <>
              <Field label="Debit card number" value={form.card_number} onChange={(v) => up("card_number", v)} testid="securities-payout-card" />
              <Field label="Expiry (MM/YY)" value={form.card_exp} onChange={(v) => up("card_exp", v)} testid="securities-payout-exp" />
            </>
          )}
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} data-testid="securities-payout-submit" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-6 py-2.5 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Save payout method</button>
          </div>
        </form>
      )}
    </section>
  );
};

const BorrowRequest = ({ onBack }) => {
  const [form, setForm] = useState({ counterparty_name: "", counterparty_entity: "", counterparty_email: "", counterparty_phone: "", counterparty_address: "", amount: "", instrument_type: "letter_of_credit", term_days: 90, purpose: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/securities/loan-requests`, {
        counterparty_name: form.counterparty_name, counterparty_entity: form.counterparty_entity || null,
        counterparty_email: form.counterparty_email, counterparty_phone: form.counterparty_phone || null,
        counterparty_address: form.counterparty_address || null, amount: parseFloat(form.amount),
        instrument_type: form.instrument_type, term_days: parseInt(form.term_days, 10), purpose: form.purpose || null,
      });
      setDone(true);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  if (done) return (
    <main className="px-6 py-20 max-w-md mx-auto text-center" data-testid="securities-borrow-done">
      <BadgeCheck size={40} className="text-[#3DBB8E] mx-auto mb-4" />
      <h2 className="font-serif text-2xl">Request submitted</h2>
      <p className="text-[#9A9A90] text-sm mt-3">Our securities desk will match your request to an eligible lender and email you a Securities Loan Agreement to review and e-sign.</p>
      <button onClick={onBack} className="mt-6 text-[12px] uppercase tracking-[0.14em] text-[#C9A227] hover:text-[#e0b62e]">← Back to home</button>
    </main>
  );

  return (
    <main className="px-6 py-14 max-w-2xl mx-auto" data-testid="securities-borrow-request">
      <button onClick={onBack} className="text-[12px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4] mb-6">← Back</button>
      <h1 className="font-serif text-3xl">Request to Borrow Collateral</h1>
      <p className="text-[#9A9A90] text-sm mt-3 mb-8 max-w-xl">Counterparties can request to borrow securities collateral directly. Our desk matches your request to an eligible lender; you'll receive a Securities Loan Agreement to e-sign, and the lending fee is collected securely on-platform.</p>
      <form onSubmit={submit} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6 grid sm:grid-cols-2 gap-4" data-testid="securities-borrow-form">
        <Field label="Your name" value={form.counterparty_name} onChange={(v) => up("counterparty_name", v)} testid="borrow-name" required />
        <Field label="Entity / firm" value={form.counterparty_entity} onChange={(v) => up("counterparty_entity", v)} testid="borrow-entity" />
        <Field label="Email" type="email" value={form.counterparty_email} onChange={(v) => up("counterparty_email", v)} testid="borrow-email" required />
        <Field label="Phone" value={form.counterparty_phone} onChange={(v) => up("counterparty_phone", v)} testid="borrow-phone" />
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">Instrument requested</span>
          <select value={form.instrument_type} onChange={(e) => up("instrument_type", e.target.value)} data-testid="borrow-instrument"
            className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
            {ACCOUNT_TYPES.map((t) => <option key={t.k} value={t.k}>{t.t}</option>)}
          </select>
        </label>
        <Field label="Amount (USD)" type="number" value={form.amount} onChange={(v) => up("amount", v)} testid="borrow-amount" required />
        <Field label="Term (days)" type="number" value={form.term_days} onChange={(v) => up("term_days", v)} testid="borrow-term" />
        <Field label="Purpose" value={form.purpose} onChange={(v) => up("purpose", v)} testid="borrow-purpose" />
        {error && <div className="sm:col-span-2 text-[#FF8585] text-[12px]" data-testid="borrow-error">{error}</div>}
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} data-testid="borrow-submit" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-7 py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Submit borrow request</button>
        </div>
      </form>
    </main>
  );
};

